import {
  ChevronLeft,
  ChevronRight,
  Close,
  CloudDownload,
  Delete,
  OpenInNew
} from '@mui/icons-material';
import {
  Badge,
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  Tab,
  Tabs,
  Typography
} from '@mui/material';
import { styled } from '@mui/material/styles';
import type {
  AttachmentResponse,
  FileData,
  GetPurchaseOrders,
  POModal,
  PoModalModifiers
} from 'api/types';
import InfoIcon from 'assets/images/info-icon-confirmation.svg';
import AWS from 'aws-sdk';
import PurchaseOrderPDFDownload from 'components/PurchaseOrderPDFDownload';
import config from 'config';
import PoData from 'pages/ProcurementWorkspace/components/PoData';
import { useEffect, useState } from 'react';
import { useProcurementWorkspaceStore } from 'store/index';
import { deliveryAddresses } from 'utils/constants';
import { dateFormatter } from 'utils/index';
import { FileUploader } from './FileUploadModal';
import CustomButton from './NewLayout/Button';
import CustomDialog from './NewLayout/Dialog';

interface AttachmentsTabModalProps {
  isOpen: boolean;
  onClose: () => void;
  purchaseOrderData?: GetPurchaseOrders | POModal;
  tableId?: string;
  attachments?: AttachmentResponse[];
  onUpload?: (fileData: FileData) => Promise<void>;
  onDelete?: (attachmentId: number) => Promise<void>;
}

interface ViewerState {
  isOpen: boolean;
  url: string;
  type: string;
  currentIndex: number;
}

export default function AttachmentsTabModal({
  isOpen,
  onClose,
  purchaseOrderData,
  tableId,
  attachments,
  onUpload,
  onDelete
}: AttachmentsTabModalProps) {
  const [tabValue, setTabValue] = useState(0);
  const [viewer, setViewer] = useState<ViewerState>({
    isOpen: false,
    url: '',
    type: '',
    currentIndex: 0
  });
  const [isUploading, setIsUploading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingIndex, setLoadingIndex] = useState<number | null>(null);
  const [downloadingIndex, setDownloadingIndex] = useState<number | null>(null);
  const [attachmentsList, setAttachmentsList] = useState<AttachmentResponse[]>([]);
  const [viewerUrls, setViewerUrls] = useState<Map<number, string>>(new Map());
  const [fileData, setFileData] = useState<FileData>({
    file: null,
    fileName: ''
  });
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteLoader, setDeleteLoader] = useState(false);
  const [selectedAttachmentForDelete, setSelectedAttachmentForDelete] =
    useState<AttachmentResponse | null>(null);

  const updatePoShipping = useProcurementWorkspaceStore((state) => state.updatePoShipping);

  useEffect(() => {
    if (purchaseOrderData) {
      const poModifiers: PoModalModifiers = {
        shipping_cost: purchaseOrderData?.shipping_cost || '0',
        estimated_taxes: purchaseOrderData?.estimated_taxes || '0',
        deposit: purchaseOrderData?.deposit || '0',
        refund: purchaseOrderData?.refund || '0',
        discount: purchaseOrderData?.discount || '0'
      };
      updatePoShipping(poModifiers);
    }

    if (attachments) {
      setAttachmentsList(attachments);
    }

    purchaseOrderData ? setTabValue(0) : setTabValue(1);
  }, [purchaseOrderData, attachments]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const getFileExtension = (url: string): string => {
    const parts = url.split('.');
    return parts.length > 1 ? parts.pop()?.toLowerCase() || '' : '';
  };

  const downloadAttachmentFile = async (attachment: AttachmentResponse) => {
    if (!attachment.uri) return;

    // If URL already exists in cache, use it for download
    const cachedUrl = viewerUrls.get(attachment.id);
    if (cachedUrl) {
      const link = document.createElement('a');
      link.href = cachedUrl;
      link.download = attachment.uri.split('/').pop() || 'document';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      return;
    }

    setDownloadingIndex(attachment.id);

    AWS.config.update({
      accessKeyId: localStorage.getItem('accessKeyId') ?? '',
      secretAccessKey: localStorage.getItem('secretAccessKey') ?? '',
      sessionToken: localStorage.getItem('sessionToken') ?? '',
      region: config.awsRegion
    });

    const s3 = new AWS.S3();
    const params = {
      Bucket: config.awsS3AssetsBucketName,
      Key: attachment.uri
    };

    const extension = getFileExtension(attachment.uri).toLowerCase();

    s3.getObject(params, (err, data) => {
      setDownloadingIndex(null);

      if (err) {
        console.error('Error fetching file from S3:', err);
        alert('Failed to download the attachment. Please try again later.');
      } else if (data.Body) {
        let mimeType: string;
        if (extension === 'pdf') {
          mimeType = 'application/pdf';
        } else if (['jpg', 'jpeg', 'png', 'gif'].includes(extension)) {
          mimeType = `image/${extension}`;
        } else {
          mimeType = 'application/octet-stream';
        }

        const blob = new Blob([data.Body as BlobPart], { type: mimeType });
        const objectUrl = URL.createObjectURL(blob);

        // Cache the URL for future use
        setViewerUrls((prev) => new Map(prev).set(attachment.id, objectUrl));

        // Download the file
        const link = document.createElement('a');
        link.href = objectUrl;
        link.download = attachment.uri.split('/').pop() || 'document';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        console.error('No data.Body found in S3 response');
        alert('The file appears to be empty or corrupted.');
      }
    });
  };

  const viewAttachment = async (attachment: AttachmentResponse, index: number) => {
    if (!attachment.uri) return;

    // If URL already exists in cache, use it
    const cachedUrl = viewerUrls.get(attachment.id);
    if (cachedUrl) {
      const extension = getFileExtension(attachment.uri).toLowerCase();
      let viewerType: string;
      if (extension === 'pdf') {
        viewerType = 'pdf';
      } else if (['jpg', 'jpeg', 'png', 'gif'].includes(extension)) {
        viewerType = 'image';
      } else {
        viewerType = 'other';
      }

      setViewer({
        isOpen: true,
        url: cachedUrl,
        type: viewerType,
        currentIndex: index
      });
      return;
    }

    setLoadingIndex(attachment.id);
    setIsLoading(true);

    const extension = getFileExtension(attachment.uri).toLowerCase();

    AWS.config.update({
      accessKeyId: localStorage.getItem('accessKeyId') ?? '',
      secretAccessKey: localStorage.getItem('secretAccessKey') ?? '',
      sessionToken: localStorage.getItem('sessionToken') ?? '',
      region: config.awsRegion
    });

    const s3 = new AWS.S3();
    const params = {
      Bucket: config.awsS3AssetsBucketName,
      Key: attachment.uri
    };

    let viewerType: string;
    if (extension === 'pdf') {
      viewerType = 'pdf';
    } else if (['jpg', 'jpeg', 'png', 'gif'].includes(extension)) {
      viewerType = 'image';
    } else {
      viewerType = 'other';
    }

    s3.getObject(params, (err, data) => {
      setIsLoading(false);
      setLoadingIndex(null);

      if (err) {
        console.error('Error fetching file from S3:', err);
        alert('Failed to load the attachment. Please try again later.');
      } else if (data.Body) {
        const mimeType =
          viewerType === 'pdf'
            ? 'application/pdf'
            : viewerType === 'image'
            ? `image/${extension}`
            : 'application/octet-stream';

        const blob = new Blob([data.Body as BlobPart], { type: mimeType });
        const objectUrl = URL.createObjectURL(blob);

        // Cache the URL
        setViewerUrls((prev) => new Map(prev).set(attachment.id, objectUrl));

        setViewer({
          isOpen: true,
          url: objectUrl,
          type: viewerType,
          currentIndex: index
        });

        console.log('File loaded successfully:', objectUrl);
      } else {
        console.error('No data.Body found in S3 response');
        alert('The file appears to be empty or corrupted.');
      }
    });
  };

  const navigateCarousel = (direction: 'prev' | 'next') => {
    const newIndex =
      direction === 'prev'
        ? (viewer.currentIndex - 1 + attachmentsList.length) % attachmentsList.length
        : (viewer.currentIndex + 1) % attachmentsList.length;

    const nextAttachment = attachmentsList[newIndex];
    viewAttachment(nextAttachment, newIndex);
  };

  const handleFileChange = (newFileData: FileData) => {
    setFileData(newFileData);
  };

  const handleUpload = async () => {
    if (!fileData.file || !onUpload) return;

    try {
      setIsUploading(true);
      await onUpload(fileData);
      setFileData({
        file: null,
        fileName: ''
      });
    } catch (error) {
      console.error('Error uploading file:', error);
      alert('Failed to upload file. Please try again.');
    } finally {
      setIsUploading(false);
      onClose();
    }
  };

  const handleOpenInNewTab = () => {
    if (viewer.url) {
      window.open(viewer.url, '_blank');
    }
  };

  const handleDownload = () => {
    if (viewer.url && attachmentsList[viewer.currentIndex]) {
      const link = document.createElement('a');
      link.href = viewer.url;
      link.download = attachmentsList[viewer.currentIndex].uri.split('/').pop() || 'document';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleDelete = async () => {
    setDeleteModalOpen(true);
  };

  const handleCardDelete = (attachment: AttachmentResponse, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent card click
    setSelectedAttachmentForDelete(attachment);
    setDeleteModalOpen(true);
  };

  const handleCardDownload = (attachment: AttachmentResponse, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent card click
    downloadAttachmentFile(attachment);
  };

  const onDeleteConfirmed = async () => {
    if (!onDelete) return;

    const attachmentToDelete = selectedAttachmentForDelete || attachmentsList[viewer.currentIndex];
    if (!attachmentToDelete) return;

    try {
      setDeleteLoader(true);
      await onDelete(attachmentToDelete.id);

      // Remove from cache
      const newViewerUrls = new Map(viewerUrls);
      newViewerUrls.delete(attachmentToDelete.id);
      setViewerUrls(newViewerUrls);

      // Remove from list
      const newAttachmentsList = attachmentsList.filter((att) => att.id !== attachmentToDelete.id);
      setAttachmentsList(newAttachmentsList);

      // If deleting from viewer, handle navigation
      if (viewer.isOpen && attachmentToDelete.id === attachmentsList[viewer.currentIndex]?.id) {
        if (newAttachmentsList.length === 0) {
          setViewer({ isOpen: false, url: '', type: '', currentIndex: 0 });
        } else {
          const nextIndex =
            viewer.currentIndex >= newAttachmentsList.length ? 0 : viewer.currentIndex;
          viewAttachment(newAttachmentsList[nextIndex], nextIndex);
        }
      }

      setDeleteModalOpen(false);
      setSelectedAttachmentForDelete(null);
    } catch (error) {
      console.error('Error deleting attachment:', error);
      alert('Failed to delete attachment. Please try again.');
    } finally {
      setDeleteLoader(false);
    }
  };

  const closeViewer = () => {
    setViewer({ isOpen: false, url: '', type: '', currentIndex: 0 });
  };

  // Clean up object URLs on unmount
  useEffect(() => {
    () => {
      viewerUrls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, []);

  return (
    <>
      <Dialog
        open={isOpen}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{ style: { maxHeight: '80vh' } }}>
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="h6" component="div">
              Attachments
            </Typography>
            <IconButton onClick={onClose} size="small">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>

        <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 3 }}>
          <Tabs value={tabValue} onChange={handleTabChange} aria-label="attachment tabs">
            <StyledTab label="Purchase Order" id="tab-0" />
            <StyledTab
              label={
                <Badge badgeContent={attachmentsList.length} color="primary">
                  Receipts/Invoices
                </Badge>
              }
              id="tab-1"
            />
          </Tabs>
        </Box>

        <DialogContent>
          {tabValue === 0 && (
            <Box mt={2}>
              {purchaseOrderData &&
              (purchaseOrderData?.subRows?.length > 0 ||
                purchaseOrderData?.purchase_order_items?.length > 0) &&
              purchaseOrderData.vendor &&
              tableId ? (
                <PurchaseOrder style={{ marginTop: 2 }} elevation={1}>
                  {/* Download Purchase Order Button Starts */}
                  <Box width="100%">
                    <Box display="flex" justifyContent="flex-end" mb={1}>
                      <PurchaseOrderPDFDownload
                        data={purchaseOrderData.subRows}
                        vendorBillingData={purchaseOrderData.vendor}
                        deliveryAddress={purchaseOrderData.delivery_address || ''}
                        poModifiers={{
                          shipping_cost: purchaseOrderData.shipping_cost,
                          estimated_taxes: purchaseOrderData.estimated_taxes,
                          deposit: purchaseOrderData.deposit,
                          refund: purchaseOrderData.refund,
                          discount: purchaseOrderData.discount
                        }}
                        specialInstructions={purchaseOrderData.instructions || ''}
                        poResponse={{
                          purchase_order_number: String(purchaseOrderData.purchase_order_number),
                          purchase_order_id: String(purchaseOrderData.purchase_order_id)
                        }}
                        requester={purchaseOrderData.requester || ''}
                        poDate={dateFormatter(
                          purchaseOrderData.created_at || new Date().toLocaleDateString(),
                          'date'
                        )}
                        purchaseDate={purchaseOrderData.purchase_date || ''}
                        paymentTerms={purchaseOrderData.payment_terms || ''}
                      />
                    </Box>
                  </Box>
                  {/* Download Purchase Order Button Ends */}

                  {/* Purchase Order View Component Starts */}
                  <PoData
                    data={
                      purchaseOrderData.subRows || purchaseOrderData?.purchase_order_items || []
                    }
                    vendorBillingData={purchaseOrderData.vendor}
                    deliveryAddress={purchaseOrderData.delivery_address || deliveryAddresses[0]?.id}
                    poModifiers={{
                      shipping_cost: purchaseOrderData.shipping_cost,
                      estimated_taxes: purchaseOrderData.estimated_taxes,
                      deposit: purchaseOrderData.deposit,
                      refund: purchaseOrderData.refund,
                      discount: purchaseOrderData.discount
                    }}
                    poNum={String(purchaseOrderData.purchase_order_number)}
                    poDate={purchaseOrderData.created_at}
                    purchaseDate={purchaseOrderData.purchase_date || ''}
                    tableId={tableId}
                    paymentTerms={purchaseOrderData.payment_terms || ''}
                    selectedCreditCard={
                      purchaseOrderData?.credit_card?.credit_card_display_name || ''
                    }
                    requester={purchaseOrderData.requester || ''}
                    specialInstructions={purchaseOrderData.instructions || ''}
                  />
                  {/* Purchase Order View Component Ends */}
                </PurchaseOrder>
              ) : (
                <Grid container direction="column" spacing={2} alignItems="center" py={4}>
                  <Grid item>
                    <Typography variant="body1" color="textSecondary" align="center">
                      No system generated Purchase Order.
                    </Typography>
                  </Grid>
                </Grid>
              )}
            </Box>
          )}

          {tabValue === 1 && (
            <Box mt={2}>
              {attachmentsList.length > 0 ? (
                <Box>
                  {/* Attachments Grid */}
                  <Grid container spacing={2} mb={2} justifyContent="center">
                    {attachmentsList.map((attachment, index) => (
                      <Grid item xs={6} sm={4} md={3} key={attachment.id}>
                        <AttachmentCard
                          elevation={1}
                          onClick={() => viewAttachment(attachment, index)}>
                          <Box
                            position="relative"
                            height="100px"
                            display="flex"
                            alignItems="center"
                            justifyContent="center">
                            {loadingIndex === attachment.id ? (
                              <CircularProgress size={30} />
                            ) : (
                              <Box textAlign="center">
                                <Typography variant="body2" fontWeight="bold">
                                  {getFileExtension(attachment.uri).toUpperCase() || 'FILE'}
                                </Typography>
                                <Typography variant="caption" color="textSecondary">
                                  {(() => {
                                    const fileNameWithExt =
                                      attachment.uri
                                        .split('/')
                                        .pop()
                                        ?.split('_')
                                        .slice(1)
                                        .join('_') || '';
                                    const fileName = fileNameWithExt.replace(/\.[^/.]+$/, ''); // remove extension
                                    return fileName.length > 15
                                      ? fileName.substring(0, 15) + '...'
                                      : fileName;
                                  })()}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                          <Box
                            p={1}
                            borderTop="1px solid #e0e0e0"
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center">
                            <Typography variant="caption" color="textSecondary">
                              {new Date(attachment.created_at).toLocaleDateString()}
                            </Typography>
                            <Box display="flex" alignItems="center" gap={0.5}>
                              <CardActionButton
                                size="small"
                                title="Download"
                                onClick={(e) => handleCardDownload(attachment, e)}
                                disabled={downloadingIndex === attachment.id}>
                                {downloadingIndex === attachment.id ? (
                                  <CircularProgress size={18} color="inherit" />
                                ) : (
                                  <CloudDownload sx={{ fontSize: 18 }} />
                                )}
                              </CardActionButton>
                              {!attachment.uri.includes('verfyi_images') && (
                                <CardActionButton
                                  size="small"
                                  title="Delete"
                                  onClick={(e) => handleCardDelete(attachment, e)}
                                  sx={{ color: '#f44336' }}>
                                  <Delete sx={{ fontSize: 18 }} />
                                </CardActionButton>
                              )}
                            </Box>
                          </Box>
                        </AttachmentCard>
                      </Grid>
                    ))}
                  </Grid>

                  {/* File Upload Section */}
                  {onUpload && (
                    <Box mt={3} pt={2} borderTop="1px solid #e0e0e0">
                      <Typography variant="subtitle1" mb={2}>
                        Upload New Attachment
                      </Typography>
                      <FileUploader onFileChange={handleFileChange} />
                      <Box mt={2} display="flex" justifyContent="flex-end">
                        <CustomButton
                          label={isUploading ? 'Uploading...' : 'Upload'}
                          onClick={handleUpload}
                          disabled={!fileData.file || isUploading}
                          endIcon={
                            isUploading ? <CircularProgress size={20} color="inherit" /> : undefined
                          }
                        />
                      </Box>
                    </Box>
                  )}
                </Box>
              ) : (
                <Box>
                  <Grid container direction="column" spacing={2} alignItems="center" py={4}>
                    <Grid item>
                      <Typography variant="body1" color="textSecondary" align="center">
                        No attachments found.
                      </Typography>
                    </Grid>
                  </Grid>

                  <FileUploader onFileChange={handleFileChange} />
                  <Box mt={2} display="flex" justifyContent="flex-end">
                    <CustomButton
                      label={isUploading ? 'Uploading...' : 'Upload'}
                      onClick={handleUpload}
                      disabled={!fileData.file || isUploading}
                      endIcon={
                        isUploading ? <CircularProgress size={20} color="inherit" /> : undefined
                      }
                    />
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Attachment Viewer Dialog */}
      <Dialog
        open={viewer.isOpen}
        onClose={closeViewer}
        maxWidth="lg"
        fullWidth
        PaperProps={{ style: { maxHeight: '90vh' } }}>
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center" gap={2}>
              <Typography variant="h6">
                Attachment {viewer.currentIndex + 1} of {attachmentsList.length}
              </Typography>
              {attachmentsList[viewer.currentIndex] && (
                <Typography variant="body2" color="textSecondary">
                  {attachmentsList[viewer.currentIndex].uri.split('/').pop()}
                </Typography>
              )}
            </Box>
            <Box display="flex" alignItems="center" gap={1}>
              {/* Carousel Navigation */}
              {attachmentsList.length > 1 && (
                <>
                  <ActionButton
                    size="small"
                    title="Previous"
                    onClick={() => navigateCarousel('prev')}>
                    <ChevronLeft />
                  </ActionButton>
                  <ActionButton size="small" title="Next" onClick={() => navigateCarousel('next')}>
                    <ChevronRight />
                  </ActionButton>
                </>
              )}

              {/* Action Buttons */}
              <ActionButton
                size="small"
                title="Open in new tab"
                onClick={handleOpenInNewTab}
                disabled={!viewer.url}>
                <OpenInNew />
              </ActionButton>
              <ActionButton
                size="small"
                title="Download"
                onClick={handleDownload}
                disabled={!viewer.url}>
                <CloudDownload />
              </ActionButton>
              {attachmentsList[viewer.currentIndex] &&
                !attachmentsList[viewer.currentIndex].uri.includes('verfyi_images') && (
                  <ActionButton size="small" title="Delete" onClick={handleDelete}>
                    <Delete />
                  </ActionButton>
                )}
              <ActionButton size="small" onClick={closeViewer}>
                <Close />
              </ActionButton>
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent>
          {viewer.url ? (
            viewer.type === 'pdf' ? (
              <object
                data={viewer.url}
                type="application/pdf"
                style={{ width: '100%', height: '70vh' }}>
                <Typography>
                  PDF cannot be displayed.{' '}
                  <a href={viewer.url} target="_blank" rel="noopener noreferrer">
                    Download instead
                  </a>
                </Typography>
              </object>
            ) : viewer.type === 'image' ? (
              <Box position="relative" textAlign="center">
                <Box overflow="auto">
                  <img
                    src={viewer.url}
                    alt="Document"
                    style={{
                      transformOrigin: 'center center',
                      transition: 'transform 0.2s ease-in-out',
                      maxWidth: '100%',
                      maxHeight: '70vh',
                      objectFit: 'contain'
                    }}
                  />
                </Box>
              </Box>
            ) : (
              <Typography>Unsupported file type</Typography>
            )
          ) : (
            <Box display="flex" justifyContent="center" alignItems="center" height="50vh">
              <Box textAlign="center">
                <CircularProgress color="primary" />
                <Typography variant="body1" mt={2}>
                  Loading attachment...
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <CustomDialog
        open={deleteModalOpen}
        closeDialog={() => {
          setDeleteModalOpen(false);
          setSelectedAttachmentForDelete(null);
        }}
        maxWidth="xs"
        loading={deleteLoader}
        title={
          <Typography>
            <img src={InfoIcon} style={{ marginRight: '10px' }} alt="Info" /> Confirm Deletion
          </Typography>
        }
        content={
          <>
            <Typography variant="body2" sx={{ fontSize: '15px !important' }}>
              This action will permanently delete this attachment.
            </Typography>
            <Typography variant="body2" sx={{ fontSize: '15px !important' }}>
              Would you like to proceed?
            </Typography>
          </>
        }
        actions={
          <>
            <CustomButton
              variant="outlined"
              label="No"
              onClick={() => {
                setDeleteModalOpen(false);
                setSelectedAttachmentForDelete(null);
              }}
            />
            <CustomButton type="primary" label="Yes" onClick={onDeleteConfirmed} />
          </>
        }
      />
    </>
  );
}

// Styled components
const StyledTab = styled(Tab)(({ theme }) => ({
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '16px',
  minWidth: 'unset',
  padding: '12px 24px',
  color: theme.palette.text.secondary,
  '&.Mui-selected': {
    color: theme.palette.primary.main
  }
}));

const ActionButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.primary.main,
  padding: '6px',
  '&:hover': {
    backgroundColor: 'rgba(0, 0, 0, 0.04)'
  },
  '&.Mui-disabled': {
    color: theme.palette.action.disabled
  }
}));

const AttachmentCard = styled(Paper)(({ theme }) => ({
  cursor: 'pointer',
  border: '1px solid #e0e0e0',
  borderRadius: '8px',
  overflow: 'hidden',
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    boxShadow: theme.shadows[4],
    transform: 'translateY(-2px)'
  }
}));

const CardActionButton = styled(IconButton)(({ theme }) => ({
  padding: '2px',
  color: theme.palette.text.secondary,
  '&:hover': {
    color: theme.palette.primary.main,
    backgroundColor: 'rgba(0, 0, 0, 0.04)'
  },
  '&.Mui-disabled': {
    color: theme.palette.action.disabled
  }
}));

const PurchaseOrder = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(1),
  marginBottom: theme.spacing(2),
  backgroundColor: '#f9f9f9',
  border: '1px solid #e0e0e0'
}));
